import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Platform,
} from 'react-native'
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react-native'
import theme from '../theme'

type ToastType = 'success' | 'error' | 'info'

interface ToastMessage {
  id: number
  type: ToastType
  title: string
  message?: string
  duration?: number
}

interface ToastContextType {
  toast: (type: ToastType, title: string, message?: string, duration?: number) => void
  success: (title: string, message?: string) => void
  error: (title: string, message?: string) => void
  info: (title: string, message?: string) => void
}

const ToastContext = createContext<ToastContextType>({
  toast: () => {},
  success: () => {},
  error: () => {},
  info: () => {},
})

export const useToast = () => useContext(ToastContext)

let nextId = 0

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const toast = useCallback((type: ToastType, title: string, message?: string, duration?: number) => {
    const id = nextId++
    setToasts(prev => [...prev, { id, type, title, message, duration: duration || 3000 }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{
      toast,
      success: (title, message) => toast('success', title, message),
      error: (title, message) => toast('error', title, message),
      info: (title, message) => toast('info', title, message),
    }}>
      {children}
      <View style={styles.container} pointerEvents="box-none">
        {toasts.map(t => (
          <ToastItem key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </View>
    </ToastContext.Provider>
  )
}

function ToastItem({ item, onDismiss }: { item: ToastMessage; onDismiss: () => void }) {
  const opacity = useRef(new Animated.Value(0)).current
  const translateY = useRef(new Animated.Value(-20)).current

  useEffect(() => {
    // Slide in
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 250, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: 250, useNativeDriver: true }),
    ]).start()

    // Auto dismiss
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }),
        Animated.timing(translateY, { toValue: -20, duration: 200, useNativeDriver: true }),
      ]).start(() => onDismiss())
    }, item.duration || 3000)

    return () => clearTimeout(timer)
  }, [])

  const config = TOAST_STYLES[item.type]
  const Icon = config.icon

  return (
    <Animated.View
      style={[
        styles.toast,
        { backgroundColor: config.bg, borderLeftColor: config.accent },
        { opacity, transform: [{ translateY }] },
      ]}
    >
      <View style={[styles.iconWrap, { backgroundColor: config.iconBg }]}>
        <Icon size={18} color={config.accent} />
      </View>
      <View style={styles.textWrap}>
        <Text style={[styles.title, { color: config.titleColor }]}>{item.title}</Text>
        {item.message ? <Text style={styles.message}>{item.message}</Text> : null}
      </View>
      <TouchableOpacity onPress={onDismiss} style={styles.closeBtn}>
        <X size={16} color={theme.colors.text.secondary} />
      </TouchableOpacity>
    </Animated.View>
  )
}

const TOAST_STYLES = {
  success: {
    bg: '#F0F7EF',
    accent: theme.colors.brand.primary,
    iconBg: `${theme.colors.brand.primary}20`,
    titleColor: '#3E5A3A',
    icon: CheckCircle,
  },
  error: {
    bg: '#FEF2F2',
    accent: '#D32F2F',
    iconBg: '#FECACA',
    titleColor: '#991B1B',
    icon: AlertCircle,
  },
  info: {
    bg: '#F0F4F8',
    accent: '#5D7C89',
    iconBg: '#D1E3EB',
    titleColor: '#3A5565',
    icon: Info,
  },
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 20 : 56,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 9999,
    ...(Platform.OS === 'web' ? { pointerEvents: 'box-none' as any } : {}),
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '90%',
    maxWidth: 480,
    borderRadius: 14,
    borderLeftWidth: 4,
    padding: 14,
    marginBottom: 8,
    gap: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }
      : { elevation: 6 }),
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  textWrap: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
  },
  message: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginTop: 2,
    lineHeight: 18,
  },
  closeBtn: {
    padding: 4,
  },
})
